import noop from 'lodash/noop';
import {
  createReadonlyObservable,
  ReadonlyObservable,
} from '@paradigm/utils/src/createObservable';

import { subscribeWebSocket } from '#/websocket/useWsInstance';
import {
  createDrfqWebSocket,
  DrfqWebSocket,
} from '#/unified-rfqs/repositories/drfq-ws';
import {
  DrfqNotification,
  DrfqNotificationByTopic,
} from '#/unified-rfqs/repositories/drfq-ws-types';
import RfqsStore from '#/unified-rfqs/store/rfqs';
import { DrfqOrderbooksStore } from '#/unified-rfqs/store/drfq-orderbooks';
import OrdersStore from '#/unified-rfqs/store/orders';
import TradesStore from '#/unified-rfqs/store/trades';
import TradeTapeStore from '#/unified-rfqs/store/trade-tape';
import { DrfqOrdersStore } from '#/unified-rfqs/store/drfq-orders';
import { DrfqDraftsStore } from '#/unified-rfqs/store/drfq-drafts';
import { markDraftWsDelivered } from '#/unified-rfqs/store/agent-draft-arrivals';

import { Order } from '#/unified-rfqs/entities/drfq/orders';
import { DrfqHistoryStore } from '../store/drfq-history';
import { Drfq, isTaker } from '../entities/drfq/domain';
import { Trade } from '../entities/drfq/trades';
import { WebSocketHandlersArgs, WebSocketStatus } from './shared';
import DrfqNotificationsService from './drfq-notifications';

type DrfqNotifHandler = (
  rfq: Drfq,
  handler: (rfq: Partial<Drfq>) => void,
) => void;

export interface CallbackHandlers extends WebSocketHandlersArgs<Trade> {
  readonly onDrfqClosed: (drfq: Drfq) => void;
  readonly handleRfqUpdated: DrfqNotifHandler;
  readonly handleRfqReceived: DrfqNotifHandler;
  readonly storeUserTrade: (rfqId: Drfq['id']) => void;
  readonly onDrfqOrderCanceled: (order: Order) => void;
  /**
   * Fires when the backend emits a SUBMITTED progress tick — only
   * happens for async-settlement venues (Paradex) where the trade
   * takes non-trivial wall-clock time to reach a terminal state.
   * Optional: consumers without the handler simply ignore the tick.
   */
  readonly onTradeSubmitted?: (trade: Trade) => void;
}

interface DrfqWsServiceDeps {
  readonly channels: readonly string[];
  readonly rfqStore: RfqsStore;
  readonly drfqDraftsStore: DrfqDraftsStore;
  readonly orderbooksStore: DrfqOrderbooksStore;
  readonly drfqOrdersStore: DrfqOrdersStore;
  readonly drfqHistoryStore: DrfqHistoryStore;
  readonly ordersStore: OrdersStore;
  readonly tradesStore: TradesStore;
  readonly tradeTapeStore: TradeTapeStore;
  readonly drfqNotificationService: DrfqNotificationsService;
  readonly handlers: CallbackHandlers;
  pinRfq: (rfqId: string | number) => void;
}

export interface DrfqWsService {
  readonly wsStatus: ReadonlyObservable<WebSocketStatus>;
  teardown(): void;
}

export function createDrfqWsService(deps: DrfqWsServiceDeps): DrfqWsService {
  return new DrfqWsServiceImpl(deps);
}

export function createDrfqWsStub(): DrfqWsService {
  const [wsStatus] = createReadonlyObservable('drfq-ws-status', INITIAL_STATUS);
  return { wsStatus, teardown: noop };
}

const INITIAL_STATUS: WebSocketStatus = {
  error: '',
  connected: false,
  subscribing: false,
  subscribed: false,
};

class DrfqWsServiceImpl implements DrfqWsService {
  readonly wsStatus: ReadonlyObservable<WebSocketStatus>;

  private readonly setWsStatus: (value: WebSocketStatus) => void;
  private readonly deps: DrfqWsServiceDeps;
  private readonly ws: DrfqWebSocket;
  private subscribeController: AbortController | null = null;

  constructor(deps: DrfqWsServiceDeps) {
    this.deps = deps;

    const [wsStatus, setWsStatus] = createReadonlyObservable(
      'drfq-ws-status',
      INITIAL_STATUS,
    );
    this.wsStatus = wsStatus;
    this.setWsStatus = setWsStatus;

    this.ws = createDrfqWebSocket();
    this.ws.addEventListener('open', this.handleOpen);
    this.ws.addEventListener('close', this.handleClose);
    this.ws.addEventListener('error', this.handleError);
    this.ws.addNotificationHandler(this.handleNotification);
  }

  teardown() {
    this.ws.removeEventListener('open', this.handleOpen);
    this.ws.removeEventListener('close', this.handleClose);
    this.ws.removeEventListener('error', this.handleError);
    this.ws.removeNotificationHandler(this.handleNotification);
    this.ws.close();
    this.handleDisconnect();
  }

  private readonly handleOpen = () => {
    this.setWsStatus({
      error: '',
      connected: true,
      subscribing: true,
      subscribed: false,
    });

    this.subscribe();
  };

  private readonly handleClose = () => {
    this.handleDisconnect();
    this.setWsStatus({
      error: 'Disconnected from Paradigm servers. Trying to reconnect...',
      connected: false,
      subscribing: false,
      subscribed: false,
    });
  };

  private readonly handleError = () => {
    this.handleDisconnect();
    this.setWsStatus({
      error: 'Error connecting to Paradigm servers',
      connected: false,
      subscribing: false,
      subscribed: false,
    });
  };

  private handleDisconnect() {
    // We must abort subscribe to disallow subscription to resolve and
    // possibly overwrite the state being set on disconnection.
    this.subscribeController?.abort();

    // Once disconnected all our data must be considered outdated, as it is
    // not possible to ensure we didn't lose any notification during the period
    // we remained disconnected.
    this.deps.rfqStore.clear();
    this.deps.orderbooksStore.clear();
    this.deps.drfqOrdersStore.clear();
    this.deps.drfqHistoryStore.clear();
    this.deps.ordersStore.clear();
    // drfqDraftsStore is intentionally NOT cleared: it is primarily
    // REST-backed (the Drafts tab loads via listDrfqDrafts), so wiping it
    // on a transient WS drop would empty the user's drafts until a
    // refetch. DRAFT_ADDED is only an additive real-time signal on top.
    // The opener does not rely on this store being fresh — it dedups via
    // the WS-arrival set + claimDraftOnce, not a staleness window.
  }

  private subscribe() {
    // We must never end up into a situation where there are two concurrent
    // subscriptions in-flight. So we use controllers to abort the previous
    // (if any) and make no-op any pending promise.
    this.subscribeController?.abort();
    const controller = new AbortController();
    this.subscribeController = controller;

    subscribeWebSocket({
      ws: this.ws,
      channels: this.deps.channels,
      name: 'drfq-ws',
      signal: controller.signal,
    })
      .then((didSucceed) => {
        if (controller.signal.aborted) return;
        this.setWsStatus({
          error: didSucceed ? '' : "Couldn't subscribe to Paradigm channels",
          connected: true,
          subscribing: false,
          subscribed: didSucceed,
        });
      })
      .catch((err: Error) => {
        if (controller.signal.aborted) return;
        this.setWsStatus({
          error: err.message,
          connected: true,
          subscribing: false,
          subscribed: false,
        });
      });
  }

  private readonly handleNotification = (notification: DrfqNotification) => {
    const { drfqNotificationService } = this.deps;
    drfqNotificationService.handleNotification(notification);

    switch (notification.topic) {
      case 'rfqs':
        this.handleRfqNotification(notification);
        break;
      case 'rfq_orders':
        this.handleRfqOrdersNotification(notification);
        break;
      case 'orders':
        this.handleOrdersNotification(notification);
        break;
      case 'trades':
        this.handleTradesNotification(notification);
        break;
      case 'trade_tape':
        this.handleTradeTapeNotification(notification);
        break;
      case 'bbo':
      case 'mmp':
      case 'unknown':
        break;
      // no default
    }
  };

  private handleRfqNotification(notification: DrfqNotificationByTopic['rfqs']) {
    const {
      rfqStore,
      drfqDraftsStore,
      orderbooksStore,
      drfqHistoryStore,
      handlers,
      pinRfq,
    } = this.deps;
    const { data, event } = notification;

    switch (event) {
      case 'ADDED':
        pinRfq(data.id);
        // Notifications should not be paused in case the user is creating the RFQ
        if (isTaker(data)) {
          rfqStore.upsertMany([data]);
        } else {
          handlers.handleRfqReceived(data, (drfq) => {
            rfqStore.upsertMany([drfq as Drfq]);
          });
        }
        break;
      case 'DRAFT_ADDED':
        // Agent-created draft (taker desk only). Record it as a live-WS
        // arrival (so the opener can tell it apart from a REST rehydrate,
        // skew-free) and land it in the drafts store; a desktop observer
        // watches the store and decides whether to open the RFQ Builder
        // (matching the label token to this app instance). Do NOT pinRfq
        // or touch rfqStore — a draft is not a live RFQ.
        markDraftWsDelivered(data.id);
        drfqDraftsStore.upsertMany([data]);
        break;
      case 'REMOVED':
        // shouldn't remove from rfqStore as ordersStore depends on some information (product/description) from it to present orders.
        rfqStore.setClosedDrfq(data);
        drfqHistoryStore.setClosedDrfq(data);
        orderbooksStore.clearByRfq(data.id);
        break;
      // no default
    }
  }

  private handleRfqOrdersNotification(
    notification: DrfqNotificationByTopic['rfq_orders'],
  ) {
    const { orderbooksStore, drfqOrdersStore } = this.deps;
    const { event, data } = notification;
    const sideStore = orderbooksStore.byRfq(data.rfq_id).bySide(data.side);
    const byRfqOrderStore = drfqOrdersStore.byRfq(data.rfq_id);

    switch (event) {
      case 'ADDED':
      case 'UPDATED':
        sideStore.setOne({ ...data, isNew: true });
        break;
      case 'REMOVED':
        sideStore.removeOne(data.id, { permanent: true });

        // Only Takers receive WS DRFQ orderbook notifications
        // Previously, Takers had no resting orders. It was fine to
        // permanently remove from the OrderStore. However, for
        // Hidden Limit Orders, there is a resting order. Permanently
        // removing from the OrderStore creates a race, where a FILLED
        // event might not trigger.

        // Without permanently removing from the store, the Order will
        // still be removed by an Order cancel, or RFQ Close.

        // To reduce the risk of this change, we'll temporarily scope
        // it only to Hidden Limit Orders.

        if (data.type === 'HIDDEN') {
          byRfqOrderStore.removeOne(data.id);
        } else {
          byRfqOrderStore.removeOne(data.id, { permanent: true });
        }

      // no default
    }
  }

  private handleOrdersNotification(
    notification: DrfqNotificationByTopic['orders'],
  ) {
    const { ordersStore, drfqOrdersStore, handlers } = this.deps;
    const { data } = notification;
    const byRfqOrderStore = drfqOrdersStore.byRfq(data.rfq_id);

    ordersStore.setOne(data);
    switch (notification.event) {
      case 'NEW':
      case 'PENDING_FILL':
      case 'FILLED':
        ordersStore.setOne(data);
        byRfqOrderStore.setOne(data);
        break;
      case 'CANCELED':
        byRfqOrderStore.setOne(data);
        handlers.onDrfqOrderCanceled(data);
        ordersStore.updateClosedOrder(data);
        byRfqOrderStore.removeOne(data.id, { permanent: true });
      // no default
    }
  }

  private handleTradesNotification(
    notification: DrfqNotificationByTopic['trades'],
  ) {
    const { tradesStore, handlers } = this.deps;
    const { event, data } = notification;
    tradesStore.handleTrade(data);
    switch (event) {
      case 'PENDING_SETTLEMENT':
        break;
      case 'SUBMITTED':
        handlers.onTradeSubmitted?.(data);
        break;
      case 'FILLED':
        handlers.storeUserTrade(data.rfq_id);
        handlers.onTradeCompleted(data);
        break;
      case 'REJECTED':
        if (data.rejected_reason != null)
          handlers.onError(data.rejected_reason, undefined, 'trade');
      // no default
    }
  }

  private handleTradeTapeNotification(
    notification: DrfqNotificationByTopic['trade_tape'],
  ) {
    const { tradeTapeStore } = this.deps;
    tradeTapeStore.handleTrade(notification.data);
  }
}
